/**
 * Monarch Dashboard API Service
 *
 * Typed fetch wrapper for all GA4 analytics endpoints.
 * In production, /api/* proxies to api.lvng.ai via Netlify.
 * In dev, /api/* proxies to localhost:5002 via Vite.
 */

import type {
  GA4OverviewData,
  GA4ChannelData,
  GA4PageData,
  GA4EventData,
  GA4FunnelStage,
  GA4ScrollDepthEntry,
  GA4ClickData,
  GA4RealtimeData,
} from '@/types/dashboard';

const BASE = '/api/monarch/analytics';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
  note?: string;
  source?: string;
  totalViews?: number;
  totalEvents?: number;
}

async function fetchApi<T>(path: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  const res = await fetch(url.toString());
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.message || body.error || `API error ${res.status}`);
  }
  return res.json();
}

function dateParams(startDate?: Date, endDate?: Date): Record<string, string> {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate.toISOString().split('T')[0];
  if (endDate) params.endDate = endDate.toISOString().split('T')[0];
  return params;
}

export async function fetchOverview(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4OverviewData>('/overview', dateParams(startDate, endDate));
  return res.data;
}

export async function fetchChannels(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4ChannelData[]>('/channels', dateParams(startDate, endDate));
  return res.data;
}

export async function fetchPages(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4PageData[]>('/pages', dateParams(startDate, endDate));
  return res.data;
}

export async function fetchEvents(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4EventData[]>('/events', dateParams(startDate, endDate));
  return res.data;
}

export async function fetchFunnel(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4FunnelStage[]>('/funnel', dateParams(startDate, endDate));
  return res.data;
}

export async function fetchScrollDepth(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4ScrollDepthEntry[]>('/scroll-depth', dateParams(startDate, endDate));
  return { data: res.data, source: res.source, note: res.note };
}

export async function fetchTopClicks(startDate?: Date, endDate?: Date) {
  const res = await fetchApi<GA4ClickData[]>('/top-clicks', dateParams(startDate, endDate));
  return res.data;
}

export async function fetchRealtime() {
  const res = await fetchApi<GA4RealtimeData>('/realtime');
  return res.data;
}
